import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import LazyLoad from "react-lazy-load";

import { Box, Grid, makeStyles, Tooltip, Typography } from "@material-ui/core";

import { TextButton, Button } from "../../../../components/base";
import { ShaDowBox } from "../../../../components/boxes";
import { IconTitle } from "../../../../components/parts";
import { ActionButtonsGroup } from "../../../../components/buttonsGroup/ActionButtonsGroup";
import { COLORS } from "../../../../theme/colors";
import { PowerConsumptionUsageType } from "../../../building/tabsV2/properties/electricity/PowerConsumptionUsageType";
import { PowerConsumptionUsagePayments } from "../../../building/tabsV2/properties/electricity/PowerConsumptionPayments";
import { PublicAreaPowerCosts } from "../../../building/tabsV2/properties/electricity/PublicAreaPowerCosts";
import { capitalize, getData, getPercent, nFormatter } from "../../../../services";
import { TenancyDataGraph } from "./tenancyData/tenancyDataGraph";
import { BuildingAlert } from "../../../building/tabsV2/properties/buildingsMiantenanceAlerts";
import TenancyImg from "../../../../assets/images/icons/tenancy.svg";
import ElectricityImg from "../../../../assets/images/icons/electricity.svg";
import powerAssetRevenueImg from "../../../../assets/images/building/powerAssetRevenue.svg";
import ProfitabilityImg from "../../../../assets/images/icons/profit.svg";
import { AllBuildingsMeters } from "./AllBuildingsMeters";
import { MetersConnectionStatus } from "./AllBuildingsMetersConnectionStatus.js";
import {
  Unpaid,
  PowerConsumptionOverTimeComponent,
  PowerConsumptionCosts,
} from "../../../building/tabsV2/properties/electricity";
import {
  ProfitabilityOverTimeComponent,
  ProfitabilityComponent,
  AvgUnrealizedPraasTradingRevenues,
} from "../../../building/tabsV2/properties/buildingProfitability";
import { allBuildingsItems } from "../../../../components/parts/graphFooter";
import { PowerAssets } from "./AllBuildingsPowerAssets";
import { GrossRentalRevenuesComponent } from "../../../building/tabsV2/properties/buildingProfitability";
import { UnitsList } from "../../../building/tabsV2/units/unitsList";
import { UnitDetail } from "../../../building/tabsV2/units/unitDetail";
import { PAGES } from "../../../index";
import { useNavigate } from "react-router-dom";
import { unitListFormatter } from "../../../building/tabsV2/units/unitListRow";

// const dispatch = useDispatch();

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing(4),
  },
  titleContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: "bold",
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },
  box: {
    padding: theme.spacing(2),
  },
  unit: {
    lineHeight: 2,
    marginLeft: 4,
  },
  descContainer: {
    marginTop: theme.spacing(4),
  },
  desc: {
    marginLeft: theme.spacing(2.5),
  },
  moreSegments: {
    marginLeft: theme.spacing(0.5),
    cursor: "pointer",
  },
  tenantListButton: {
    marginLeft: theme.spacing(2.5),
  },
}));

export const PropertiesTab = () => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const [propertyInfo, setPropertyInfo] = useState();
  const [showUnitList, setShowUnitList] = useState(false);
  const [unit, setUnit] = useState();
  const navigate = useNavigate();
  const { cluster, segments, selectedCountries } = useSelector((state) => state.selectCSC);

  useEffect(() => {
    getData("/praas/v1/buildings/property-info/", setPropertyInfo);
  }, [cluster, segments, selectedCountries]);

  if (unit && showUnitList) {
    if (unit?.isGroup) {
      navigate(`${PAGES.buildings.index}/${unit.id}`);
    } else {
      return <UnitDetail unit={unit} setUnit={setUnit} setShowUnitList={setShowUnitList} />;
    }
  }

  if (showUnitList) {
    return (
      <UnitsList
        setShowUnitList={setShowUnitList}
        onSelected={setUnit}
        formatter={unitListFormatter}
      />
    );
  }

  return (
    <Box className={classes.root}>
      <Grid
        className={classes.titleContainer}
        container
        justifyContent="space-between"
        alignItems="center"
      >
        <IconTitle
          iconSrc={TenancyImg}
          title="TENANCY DATA & ANALYTICS"
          rootStyle={{
            marginTop: 0,
            marginBottom: 0,
          }}
        />
        <ActionButtonsGroup hideIntegrateBtn />
        <Button
          text="UNITS LIST"
          rootStyle={classes.tenantListButton}
          onClick={() => {
            setShowUnitList(true);
            //  setShowList(true);
            //  dispatch(getUnitsList());
          }}
        />
      </Grid>
      <ShaDowBox>
        <Grid container>
          <Grid className={classes.box} item sm={3} lg>
            <Typography>Segment</Typography>
            {propertyInfo?.segments?.length > 0 && (
              <Grid container alignItems="baseline">
                <Typography variant="h3">{(propertyInfo?.segments?.length) ? capitalize(propertyInfo.segments[0]) : "-"} </Typography>
                {propertyInfo.segments.length > 1 && (
                  <Tooltip
                    title={propertyInfo.segments.slice(1).map((segment, i) => (
                      <Typography key={i} style={{ color: COLORS.white }} variant="body2">
                        {capitalize(segment)}
                      </Typography>
                    ))}
                    placement="right"
                  >
                    <Typography className={classes.moreSegments}>
                      + {propertyInfo.segments.length - 1}
                    </Typography>
                  </Tooltip>
                )}
              </Grid>
            )}
          </Grid>

          {[
            {
              title: "Total Buildings",
              value: nFormatter(propertyInfo?.total_buildings ?? 0),
            },
            {
              title: "Total Tenants",
              value: nFormatter(propertyInfo?.total_tenants ?? 0),
            },
            {
              title: "Total Area",
              value: nFormatter(propertyInfo?.total_area ?? 0),
              unit: user?.unit_system,
            },
            {
              title: "Roof Area",
              value: nFormatter(propertyInfo?.roof_area ?? 0),
              unit: user?.unit_system,
            },
            {
              title: "Public Area",
              value: nFormatter(propertyInfo?.public_area),
              unit: `${user?.unit_system} (${getPercent(
                propertyInfo?.public_area,
                propertyInfo?.total_area,
              )}%)`,
              button: "compare",
            },
            {
              title: "Electriticy Meters",
              value: nFormatter(propertyInfo?.electricity_meters ?? 0),
            },
            {
              title: "Meters Connected",
              value: nFormatter(propertyInfo?.meters_connected ?? 0),
              unit: propertyInfo?.meters_connected
                ? `(${getPercent(
                  propertyInfo.meters_connected,
                  propertyInfo.electricity_meters,
                )}% of total)`
                : "",
            },
          ].map((entry, index) => (
            <Grid
              className={classes.box}
              key={index}
              item
              sm={3}
              lg
              style={{
                borderLeft: `1px solid ${COLORS.darks[0][10]}`,
              }}
              id={entry.title.toLowerCase().replaceAll(" ", "-").replaceAll(":", "")}
            >
              <Typography>{entry.title}</Typography>
              <Grid container>
                <Typography variant="h3">{entry.value}</Typography>
                {entry.unit && <Typography className={classes.unit}>{entry.unit}</Typography>}
              </Grid>
              {entry.button && <TextButton text={entry.button} normalFontWeight />}
            </Grid>
          ))}
        </Grid>
      </ShaDowBox>

      <Grid container spacing={3} style={{ marginTop: 26 }}>
        <Grid item xs={12}>
          <TenancyDataGraph />
        </Grid>
      </Grid>

      <IconTitle
        rootStyle={{ paddingTop: 20, marginBottom: 25 }}
        iconSrc={ElectricityImg}
        title="ELECTRICITY DATA & ANALYTICS"
      />
      <Grid container spacing={3}>
        <Grid item sm={4}>
          <LazyLoad>
            <AllBuildingsMeters />
          </LazyLoad>
        </Grid>
        <Grid item xs={8} style={{ minHeight: 337 }}>
          <LazyLoad>
            <MetersConnectionStatus />
          </LazyLoad>
        </Grid>
      </Grid>
      <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
        <Grid item sm={4}>
          <LazyLoad height="100%">
            <PowerConsumptionUsageType title="Portfolio Power Consumption - Usage type" />
          </LazyLoad>
        </Grid>
        <Grid item sm={4}>
          <LazyLoad height="100%">
            <PowerConsumptionUsagePayments title="Portfolio Power Consumption - Payments" />
          </LazyLoad>
        </Grid>
        <Grid item sm={4}>
          <LazyLoad height="100%">
            <PublicAreaPowerCosts />
          </LazyLoad>
        </Grid>
      </Grid>

      <LazyLoad>
        <Unpaid />
      </LazyLoad>

      <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
        <Grid item xs={4}>
          <LazyLoad height="100%">
            <PowerConsumptionOverTimeComponent title="Portfolio Power Consumption Over Time" />
          </LazyLoad>
        </Grid>
        <Grid item xs={8}>
          <LazyLoad height="100%">
            <PowerConsumptionCosts
              titleFirst="Avg. Portfolio Power Consumption Cost"
              items={allBuildingsItems}
            />
          </LazyLoad>
        </Grid>
      </Grid>

      {user.is_demo ? (
        <Grid>
          <IconTitle iconSrc={powerAssetRevenueImg} title="POWER ASSETS DATA & ANALYTICS" />
          <LazyLoad>
            <PowerAssets />
          </LazyLoad>
        </Grid>
      ) : (
        <Grid></Grid>
      )}

      <LazyLoad>
        <IconTitle iconSrc={ProfitabilityImg} title="PORTFOLIO PROFITABILITY" />
      </LazyLoad>

      <LazyLoad>
        <ProfitabilityOverTimeComponent />
      </LazyLoad>

      <LazyLoad>
        <ProfitabilityComponent />
      </LazyLoad>

      <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
        <Grid item xs={4}>
          <LazyLoad>
            <GrossRentalRevenuesComponent />
          </LazyLoad>
        </Grid>
        <Grid item xs={8}>
          <LazyLoad height="100%">
            <AvgUnrealizedPraasTradingRevenues />
          </LazyLoad>
        </Grid>
      </Grid>

      <Typography className={classes.title}>PORTFOLIO MAINTENANCE ALERTS</Typography>
      <BuildingAlert />
    </Box>
  );
};
